import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useKeyboardShortcuts } from './use-keyboard-shortcuts';
import { ModelScopeManager } from './model-scope.manager';
import { ActionsManager } from './actions.manager';
import {
  ActionConstructor,
  type Action as ActionType,
  type FullActionsContext,
  type Model,
  type ModelConstructor,
  ReadActionsContext,
} from './types';
import { Assert } from '@cotera/utilities';
import { useCustomWindowEvents } from './use-window-events';
import { noopAdapter, type ActionTrackingAdapter } from './action-tracking';
import { Logger, useLogger } from './logger';

export type ActionsProviderProps = {
  children: ReactNode;
  actions?: ActionType<any>[];
  modelScope?: ModelScopeManager;
  trackingAdapter?: ActionTrackingAdapter;
  logger?: Logger;
};

const useReactiveActionsSetup = (
  actions: ActionType<any>[],
  context: FullActionsContext
) => {
  useKeyboardShortcuts(
    actions
      .filter((action) => {
        return action.shortcut !== undefined;
      })
      .map((action) => ({
        shortcut: action.shortcut!,
        // Consulted before the keystroke is consumed; see `shortcutAvailable`.
        available: () => action.shortcutAvailable?.() ?? true,
        callback: async () => {
          const newCtx = ActionsManager.fromExisting(context);
          newCtx.addModels(context.getAllInScopeModels());

          if (!action.applicable(newCtx)) {
            return;
          }

          const executableAction = newCtx.getAction(
            action.constructor as ActionConstructor<any>
          );
          Assert.assert(executableAction !== null);

          await executableAction.askOrExecute({}, newCtx);
        },
      }))
  );

  useCustomWindowEvents(
    actions
      .filter((action) => action.events !== undefined)
      .flatMap(
        (action) =>
          action.events?.map((event) => {
            return {
              event,
              callback: async (event: CustomEvent) => {
                const newCtx = ActionsManager.fromExisting(context);
                newCtx.addModels(context.getAllInScopeModels());

                if (!action.applicable(newCtx)) {
                  return;
                }

                await action.askOrExecute(event.detail, newCtx);
              },
            };
          }) ?? []
      )
  );
};

const ActionsContext = createContext<FullActionsContext | null>(null);

export function ActionsRegistryProvider({
  children,
  actions = [],
  modelScope,
  trackingAdapter,
  logger: loggerProp,
}: ActionsProviderProps) {
  // Get logger from context if available, otherwise use prop or default
  const loggerFromContext = useLogger();
  const logger = loggerProp ?? loggerFromContext;

  const contextValue = useMemo(
    () =>
      new ActionsManager(actions, {
        parentModelScope: modelScope,
        trackingAdapter: trackingAdapter ?? noopAdapter,
        logger,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logger]
  );

  useReactiveActionsSetup(actions, contextValue);

  return (
    <ActionsContext.Provider value={contextValue}>
      {children}
    </ActionsContext.Provider>
  );
}

export const Actions: React.FC<{
  actions: ActionType<any>[];
  children: ReactNode;
}> = ({ actions, children }) => {
  const context = useContext(ActionsContext);

  if (!context) {
    throw new Error('Actions must be used within an ActionsProvider');
  }

  useEffect(() => {
    actions.forEach((action) => {
      context!.registerAction(action);
    });

    return () => {
      actions.forEach((action) => {
        context!.unregisterAction(action);
      });
    };
  }, [actions, context]);

  useReactiveActionsSetup(actions, context);

  return <>{children}</>;
};

function useRegisterModels<T extends Model>(
  create: () => T[],
  opts: {
    register: boolean;
    deps: any[];
  }
) {
  const context = useContext(ActionsContext);
  if (!context) {
    throw new Error('useRegisterModels must be used within an ActionsProvider');
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memodModels = useMemo(create, opts.deps);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (opts.register) {
      context.addModels(memodModels);
      memodModels.forEach((model) => {
        model.onCreate?.();
      });
    } else {
      // Remove any existing models that are no longer registered
      memodModels.forEach((model) => {
        if (
          context.getInScopeModelOfType(
            model.constructor as ModelConstructor,
            model.id
          ) &&
          !opts.register
        ) {
          context.removeModel(model.constructor as ModelConstructor, model.id);
        }
      });
    }

    setReady(true);

    return () => {
      // Cleanup by removing all models (removeModel calls dispose when ref count reaches 0)
      memodModels.forEach((model) => {
        context.removeModel(model.constructor as ModelConstructor, model.id);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memodModels, opts.register]);

  return ready;
}

/**
 * Registers models created by a factory inside the effect. When the effect runs
 * (including on remount), fresh models are created. When cleanup runs, we dispose
 * those models. This avoids the "zombie model" problem where a disposed model
 * gets re-added to context after unmount/remount (e.g. React Strict Mode).
 */
function useRegisterModelsFromFactory<T extends Model>(
  createModels: () => T[],
  deps: React.DependencyList
) {
  const context = useContext(ActionsContext);
  if (!context) {
    throw new Error('useRegisterModels must be used within an ActionsProvider');
  }

  const [ready, setReady] = useState(false);

  useEffect(() => {
    const rawModels = createModels();
    const models = context.addModels(rawModels);
    rawModels.forEach((raw, i) => {
      if (models[i] !== raw) {
        raw.dispose?.();
      } else {
        raw.onCreate?.();
      }
    });
    setReady(true);

    return () => {
      setReady(false);
      models.forEach((model) => {
        context.removeModel(model.constructor as ModelConstructor, model.id);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ready;
}

type ModelScopeProps = {
  models: Model[];
  children?: ReactNode;
};

export function ModelScope({ models, children }: ModelScopeProps) {
  const ready = useRegisterModels(() => models, {
    register: true,
    deps: [models],
  });

  if (!ready) {
    return null;
  }

  return <>{children}</>;
}

type ModelScopeFactoryProps = {
  createModels: () => Model[];
  deps: React.DependencyList;
  children?: ReactNode;
};

/**
 * Registers models created by a factory inside the effect. When the effect runs
 * (including on remount), fresh models are created. When cleanup runs, we dispose
 * those models. This avoids the "zombie model" problem where a disposed model
 * gets re-added to context after unmount/remount (e.g. React Strict Mode).
 */
export function ModelScopeFactory({
  createModels,
  deps,
  children,
}: ModelScopeFactoryProps) {
  const ready = useRegisterModelsFromFactory(createModels, deps);

  if (!ready) {
    return null;
  }

  return <>{children}</>;
}

export const useActionsContext = (): ReadActionsContext => {
  const context = useContext(ActionsContext);
  if (!context) {
    throw new Error('useActionsContext must be used within an ActionsProvider');
  }
  return context;
};

/**
 * The actions context, or null when there is no provider above.
 *
 * For surfaces that render both inside the app shell and standalone (isolated
 * tests, provider-less previews) and must degrade rather than throw — the same
 * reason `useFavorites` returns an inert state off-shell.
 */
export const useOptionalActionsContext = (): ReadActionsContext | null =>
  useContext(ActionsContext);
